import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';
import { Watermark } from './Watermark';

type GoalType =
  | 'follow'
  | 'sub'
  | 'sub.gift'
  | 'cheer'
  | 'tip'
  | 'superChat'
  | 'superSticker'
  | 'raid'
  | 'manual';

interface GoalDef {
  id: string;
  title: string;
  goalType: GoalType;
  target: number;
  color?: string;
  currentValue?: number;
  platform?: 'twitch' | 'youtube' | 'kick' | 'hypevault' | null;
}

const TYPE_LABEL: Record<GoalType, string> = {
  follow: 'Followers',
  sub: 'Subs',
  'sub.gift': 'Gift Subs',
  cheer: 'Bits',
  tip: 'Coins',
  superChat: 'SuperChats',
  superSticker: 'SuperStickers',
  raid: 'Raids',
  manual: '',
};

function deriveGoals(fields: Record<string, unknown>): GoalDef[] {
  return (fields.goals as GoalDef[] | undefined) ?? [];
}

function eventIncrement(e: StreamEvent, goal: GoalDef): number {
  if (goal.platform && e.platform !== goal.platform) return 0;
  switch (goal.goalType) {
    case 'follow':
      return e.type === 'follow' ? 1 : 0;
    case 'sub':
      return e.type === 'sub' && !e.isGift ? 1 : 0;
    case 'sub.gift':
      return e.type === 'sub' && e.isGift ? 1 : 0;
    case 'cheer':
      return e.type === 'cheer' ? e.bits : 0;
    case 'tip':
      if (e.type === 'tip') return e.coins;
      if (e.type === 'tip_received') return e.amountCents;
      return 0;
    case 'superChat':
      return e.type === 'superChat' ? e.amountCents : 0;
    case 'superSticker':
      return e.type === 'superSticker' ? e.amountCents : 0;
    case 'raid':
      return e.type === 'raid' ? 1 : 0;
    case 'manual':
      return 0;
  }
}

function formatCents(c: number): string {
  return `$${(c / 100).toFixed(2)}`;
}

function formatProgress(value: number, target: number, type: GoalType): string {
  if (type === 'tip') {
    return `${value.toLocaleString()} / ${target.toLocaleString()} coins`;
  }
  if (type === 'cheer') {
    return `${value.toLocaleString()} / ${target.toLocaleString()} bits`;
  }
  if (type === 'superChat' || type === 'superSticker') {
    return `${formatCents(value)} / ${formatCents(target)}`;
  }
  return `${value.toLocaleString()} / ${target.toLocaleString()} ${TYPE_LABEL[type].toLowerCase()}`.trim();
}

export function GoalWidget({
  fields,
  latestEvent,
  streamerTier = 'free',
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const goals = deriveGoals(fields);
  const [progress, setProgress] = useState<Record<string, number>>(() =>
    Object.fromEntries(goals.map((g) => [g.id, g.currentValue ?? 0])),
  );
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    setProgress((prev) => {
      const next = { ...prev };
      for (const g of goals) {
        const inc = eventIncrement(latestEvent, g);
        if (inc > 0) next[g.id] = Math.min(g.target, (next[g.id] ?? 0) + inc);
      }
      return next;
    });
  }, [latestEvent, goals]);

  if (goals.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'grid',
          placeItems: 'center',
          background: 'rgba(15,12,30,0.85)',
          border: '1px solid rgba(139,92,246,0.18)',
          borderRadius: 10,
          color: 'rgba(255,255,255,0.3)',
          fontSize: 12,
          fontFamily: "'Poppins', system-ui, sans-serif",
        }}
      >
        No goals configured
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
      }}
    >
      {goals.map((g, i) => {
        const value = progress[g.id] ?? 0;
        const pct = Math.min(100, (value / g.target) * 100);
        const completed = value >= g.target;
        const color = g.color ?? '#8b5cf6';
        const isLast = i === goals.length - 1;
        return (
          <div
            key={g.id}
            style={{
              background: 'linear-gradient(135deg, rgba(10,8,24,0.88), rgba(18,14,38,0.88))',
              border: completed
                ? '1px solid rgba(251,191,36,0.4)'
                : '1px solid rgba(139,92,246,0.16)',
              borderRadius: 10,
              padding: '10px 16px 12px',
              position: 'relative',
              overflow: 'hidden',
              animation: completed
                ? 'g-complete-pulse 2.8s ease-in-out infinite'
                : 'g-slide-in 0.35s ease-out both',
              boxShadow: completed ? '0 0 20px rgba(251,191,36,0.12)' : undefined,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 20,
                right: 20,
                height: 1,
                background: `linear-gradient(90deg, transparent, ${color}59, transparent)`,
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 7,
                gap: 12,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.85)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {g.title}
                {completed && (
                  <span style={{ marginLeft: 8, color: '#fbbf24', fontSize: 9 }}>★ COMPLETE</span>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.92)' }}>
                  {formatProgress(value, g.target, g.goalType)}
                </span>
                <span style={{ fontSize: 11, color, fontWeight: 600 }}>{pct.toFixed(0)}%</span>
              </div>
            </div>
            <div
              style={{
                height: 10,
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 999,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: completed
                    ? 'linear-gradient(90deg,#fbbf24,#fde047,#fbbf24)'
                    : `linear-gradient(90deg, ${color}, ${color}dd)`,
                  borderRadius: 999,
                  transition: 'width 600ms cubic-bezier(0.22,1,0.36,1)',
                  boxShadow: `0 0 12px ${color}80`,
                }}
              />
            </div>
            {isLast && streamerTier === 'free' && (
              <div
                style={{
                  marginTop: 8,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Watermark variant="inline" />
              </div>
            )}
          </div>
        );
      })}
      <style>{`
        @keyframes g-slide-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes g-complete-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(251,191,36,0.12), 0 0 0 1px rgba(251,191,36,0.08); }
          50%      { box-shadow: 0 0 32px rgba(251,191,36,0.25), 0 0 0 1px rgba(251,191,36,0.16); }
        }
      `}</style>
    </div>
  );
}
